/**
 * Magazine Card - Horizontal card with side-by-side image and text
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { ExploreEntry } from '../../Model/Explore/ExploreEntry';
import { Recipe } from '../../Model/Diet/Recipe/Recipe';
import { useEntryText } from './useEntryText';
import { getContentTypeLabel, getContentTypeColor } from './contentTypeUtils';
import { Theme } from '../../Theme/Theme';

const CardContainer = styled.div`
  display: flex;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  height: 180px;

  &:hover {
    transform: translateX(8px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);

    .image-container {
      transform: scale(1.05);
    }

    .arrow {
      transform: translateX(4px);
      opacity: 1;
    }

    .accent-line {
      width: 60px;
    }
  }

  @media (max-width: 600px) {
    height: 140px;
  }
`;

const ImageContainer = styled.div`
  width: 40%;
  min-width: 140px;
  position: relative;
  overflow: hidden;
  isolation: isolate;
`;

const Image = styled.div<{ image?: string }>`
  position: absolute;
  inset: -1px;
  background: ${({ image }) =>
    image ? `url('${image}')` : 'linear-gradient(135deg, #00dda2 0%, #007763 100%)'};
  background-size: cover;
  background-position: center;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
`;

const Content = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
`;

const AccentLine = styled.div`
  width: 40px;
  height: 3px;
  background: #00dda2;
  border-radius: 2px;
  margin-bottom: 12px;
  transition: width 0.3s ease;
`;

const TypeLabel = styled.span<{ labelColor?: string }>`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: ${({ labelColor }) => labelColor || '#00dda2'};
  margin-bottom: 8px;
`;

const Title = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  padding-right: 32px;
  line-height: 1.3;
  color: ${Theme.Colors.textColor};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Description = styled.p`
  font-size: 0.85rem;
  color: #666;
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 600px) {
    display: none;
  }
`;

const Arrow = styled.div`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.5;
  transition: all 0.3s ease;
  font-size: 20px;
  color: #00dda2;
`;

const DurationBadge = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  backdrop-filter: blur(4px);
`;

export type MagazineCardProps = {
  entry: ExploreEntry | Recipe;
  linkPrefix?: string;
};

export const MagazineCard: React.FC<MagazineCardProps> = observer(({ entry, linkPrefix = '/academy/' }) => {
  const { title, description, type, duration, imageMedium } = useEntryText(entry);
  const navigate = useNavigate();

  const handleClick = React.useCallback(() => {
    navigate(`${linkPrefix}${entry.id}`);
  }, [navigate, linkPrefix, entry]);

  return (
    <CardContainer onClick={handleClick}>
      <ImageContainer>
        <Image className="image-container" image={imageMedium} />
        {duration && duration > 0 && <DurationBadge>{duration} min</DurationBadge>}
      </ImageContainer>
      <Content>
        <AccentLine className="accent-line" />
        <TypeLabel labelColor={getContentTypeColor(type)}>{getContentTypeLabel(type)}</TypeLabel>
        <Title>{title}</Title>
        <Description>{description}</Description>
        <Arrow className="arrow">→</Arrow>
      </Content>
    </CardContainer>
  );
});
